import { Link } from 'react-router-dom'
import { ArrowRight, CheckCircle2 } from 'lucide-react'
import { Card, CardHeader } from '@/components/ui/Card'
import { countLabel } from '@/lib/format'
import type { ReviewSummary } from '@/types'

const QUEUES: Array<{ key: keyof ReviewSummary; label: string; noun: string; to: string }> = [
  { key: 'programs', label: 'Programs', noun: 'program', to: 'programs' },
  { key: 'quotes', label: 'Quotes', noun: 'quote', to: 'quotes' },
  { key: 'media', label: 'Media', noun: 'media item', to: 'media' },
  { key: 'facts', label: 'Partner facts', noun: 'fact', to: 'facts' },
]

/**
 * The mockup's separate Extraction Review Queue screen, merged in here
 * (docs/00-overview.md §4). The all-clear is one success line rather than
 * four empty cards — four zero-state cards is noise, not information.
 */
export function ReviewQueueBand({
  summary,
  clientId,
}: {
  summary: ReviewSummary | undefined
  clientId: string
}) {
  const total = summary
    ? summary.programs + summary.quotes + summary.media + summary.facts
    : 0

  return (
    <Card>
      <CardHeader
        title="Pending human sign-off"
        description="Nothing reaches the agent until it is approved here."
      />

      {summary && total === 0 ? (
        <p className="flex items-center gap-2 px-4 py-4 text-[13px] text-affirm">
          <CheckCircle2 size={15} aria-hidden />
          All extracted content reviewed.
        </p>
      ) : (
        <ul className="grid gap-px bg-rule sm:grid-cols-2 lg:grid-cols-4">
          {QUEUES.map((queue) => {
            const count = summary?.[queue.key] ?? 0
            return (
              <li key={queue.key} className="bg-surface">
                <Link
                  to={`/c/${clientId}/${queue.to}`}
                  className="flex h-full flex-col gap-1 px-4 py-3 hover:bg-canvas"
                >
                  <span className="field-label">{queue.label}</span>
                  <span className="tabular font-head text-[22px] font-semibold">{count}</span>
                  <span className="mt-auto inline-flex items-center gap-1 pt-1 text-[12px] text-brand-blue">
                    {count === 0 ? 'Nothing pending' : `Review ${countLabel(count, queue.noun)}`}
                    <ArrowRight size={11} aria-hidden />
                  </span>
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}
